// Main-thread API for image compression. Spawns a single Web Worker
// (Vite URL-based pattern) and queues compression jobs. Falls back to
// main-thread compression if the worker can't load.
//
// Lifted from ~/Desktop/Reputifly AI Local/app/src/lib/compression.ts
// Change: maxDim 1024 -> 2000 to match the server-side safe-image
// guard at /home/claude/agent/bin/safe-image (2000px long-edge cap).

export interface CompressedImage {
  blob: Blob;
  base64: string; // no data: prefix
  meta: {
    origBytes: number;
    compressedBytes: number;
    width: number;
    height: number;
    mimeType: string;
  };
}

const DEFAULTS = {
  maxDim: 2000,           // long edge max -- matches /home/claude/agent/bin/safe-image cap
  jpegQuality: 0.85,
  softCapBytes: 1.5 * 1024 * 1024, // 1.5 MB soft cap (slightly higher than AI Local since maxDim doubled)
  maxOriginalMB: 25,
};

let _worker: Worker | null = null;
let _workerOk = true;
let _seq = 0;
const _pending = new Map<string, { resolve: (r: any) => void; reject: (e: Error) => void }>();

function ensureWorker(): Worker | null {
  if (_worker) return _worker;
  if (!_workerOk) return null;
  try {
    _worker = new Worker(
      new URL("./compression.worker.ts", import.meta.url),
      { type: "module" }
    );
    _worker.addEventListener("message", (e: MessageEvent) => {
      const { id, ok, blob, meta, error } = e.data ?? {};
      const slot = _pending.get(id);
      if (!slot) return;
      _pending.delete(id);
      if (ok) slot.resolve({ blob, meta });
      else slot.reject(new Error(error || "worker_unknown"));
    });
    _worker.addEventListener("error", (err) => {
      console.warn("compression worker error", err);
      _workerOk = false;
      _worker = null;
      // Reject any in-flight
      for (const [, slot] of _pending) slot.reject(new Error("worker_crashed"));
      _pending.clear();
    });
    return _worker;
  } catch (e) {
    console.warn("compression worker unavailable", e);
    _workerOk = false;
    return null;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      // data:image/jpeg;base64,XXXX  -> strip the prefix
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(new Error("base64_read_failed"));
    reader.readAsDataURL(blob);
  });
}

async function compressOnMain(file: File | Blob): Promise<{ blob: Blob; meta: CompressedImage["meta"] }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("img_load_failed"));
      im.src = url;
    });
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    const scale = Math.min(1, DEFAULTS.maxDim / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no_2d_context");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const qualities = [DEFAULTS.jpegQuality, 0.75, 0.6];
    let blob: Blob | null = null;
    for (const q of qualities) {
      blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob((b) => res(b), "image/jpeg", q)
      );
      if (!blob) throw new Error("toBlob_null");
      if (blob.size <= DEFAULTS.softCapBytes) break;
    }
    const origSize = file instanceof File ? file.size : (file as Blob).size;
    return {
      blob: blob!,
      meta: {
        origBytes: origSize,
        compressedBytes: blob!.size,
        width: w,
        height: h,
        mimeType: "image/jpeg",
      },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Public API. Compress an image File into a JPEG blob + base64. */
export async function compressImage(file: File): Promise<CompressedImage> {
  if (file.size > DEFAULTS.maxOriginalMB * 1024 * 1024) {
    throw new Error(`file_too_large (>${DEFAULTS.maxOriginalMB} MB)`);
  }
  const worker = ensureWorker();
  let result: { blob: Blob; meta: CompressedImage["meta"] };

  if (worker) {
    const id = `c${++_seq}`;
    try {
      result = await new Promise<{ blob: Blob; meta: CompressedImage["meta"] }>(
        (resolve, reject) => {
          _pending.set(id, { resolve, reject });
          worker.postMessage({
            id,
            file,
            maxDim: DEFAULTS.maxDim,
            jpegQuality: DEFAULTS.jpegQuality,
            softCapBytes: DEFAULTS.softCapBytes,
          });
        }
      );
    } catch (e) {
      console.warn("worker compress failed, falling back to main thread", e);
      result = await compressOnMain(file);
    }
  } else {
    result = await compressOnMain(file);
  }

  const base64 = await blobToBase64(result.blob);
  return { ...result, base64 };
}

/** Whether this browser/runtime supports the worker path. */
export function workerCompressionAvailable(): boolean {
  return _workerOk && typeof Worker !== "undefined";
}
